import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  FiCheck,
  FiRefreshCw,
  FiAlertTriangle,
  FiHelpCircle,
  FiChevronLeft,
  FiClock,
  FiUser,
  FiFileText,
  FiZap,
  FiTrendingUp,
  FiSend,
  FiX,
  FiCopy,
  FiDownload,
} from "react-icons/fi";
import { RiSparklingFill } from "react-icons/ri";
import {
  useRateQuoteLink,
  type StepStatus,
  type AwaitingReviewPayload,
  type QuoteResult,
} from "./RateQuoteLinkContext";
import { useRunChat, type ConfirmOpts } from "./useRunChat";
import PLRChatView from "./PLRChatView";

const RateMIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.63 13.16c0-1.55.01-3.1 0-4.65-.01-.83.3-1.52.84-2.11.94-1.02 1.92-2 2.83-3.03.46-.52 1.69-.47 2.01.31.06.14.1.3.1.45.01 3.08.01 6.15 0 9.23-.01.79-.38 1.46-.9 2.03-.98 1.08-1.99 2.13-3.01 3.17-.5.51-1.22.58-1.63.14-.16-.18-.23-.5-.24-.76-.02-1.59-.01-3.18-.01-4.77z" fill="currentColor"/>
    <path d="M21.91 8.78c0 1.5-.01 3-.01 4.5 0 .71-.23 1.33-.71 1.84-1.05 1.12-2.09 2.25-3.17 3.35-.23.24-.58.44-.9.49-.55.09-1.01-.4-1.01-1.02 0-3.17 0-6.34.02-9.5 0-.78.29-1.45.85-2.03.9-.94 1.78-1.9 2.65-2.87.42-.47.9-.68 1.52-.46.5.17.75.51.75 1.04v4.65z" fill="currentColor"/>
    <path d="M8.87 12.29c-.08 2.46.09 2-.4 3.45-.81.88-1.63 1.76-2.46 2.62-.22.23-.49.45-.78.56-.64.26-1.28-.19-1.29-.89-.01-.8-.04-1.59.02-2.38.02-.32.18-.69.4-.93 1.25-1.39 2.54-2.75 3.82-4.13.25-.27.51-.48.9-.46.45.02.71.29.72.75 0 .52 0 1.04 0 1.41z" fill="currentColor"/>
  </svg>
);

const STEP_PILL_LABEL: Record<StepStatus, string> = {
  pending: "Pending",
  running: "In Progress",
  awaiting_input: "Needs Input",
  completed: "Completed",
  failed: "Failed",
};

const COVERAGE_LABELS: Record<string, string> = {
  bodily_injury_per_person: "BI / person",
  bodily_injury_per_accident: "BI / accident",
  property_damage: "Property damage",
  uninsured_motorist: "Uninsured motorist",
  underinsured_motorist: "Underinsured motorist",
  pip: "PIP",
  medical_payments: "Medical payments",
  comprehensive_deductible: "Comp deductible",
  collision_deductible: "Collision deductible",
  rental_reimbursement: "Rental reimbursement",
  towing: "Towing",
  term: "Term (months)",
  effective_date: "Effective date",
  prior_liability_limit: "Prior liability limit",
  prior_months_coverage: "Prior months coverage",
  residential_insurance_policy: "Residential policy",
  residential_carrier: "Residential carrier",
  interested_in_residential: "Interested in residential",
  residence_type: "Residence type",
  prior_insurance: "Prior insurance",
  prior_carrier_id: "Prior carrier ID",
};

function titlecase(s: string): string {
  return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmt$(n: number | null | undefined): string {
  if (n == null) return "—";
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const StepIcon: React.FC<{ status: StepStatus; index: number }> = ({
  status,
  index,
}) => {
  if (status === "completed") return <FiCheck size={14} />;
  if (status === "running")
    return <FiRefreshCw size={14} className="rql-row-execute__spin" />;
  if (status === "awaiting_input") return <FiHelpCircle size={14} />;
  if (status === "failed") return <FiAlertTriangle size={14} />;
  return <span className="rql-iwf__step-num-inner">{index + 1}</span>;
};

const ReviewCard: React.FC<{ review: AwaitingReviewPayload }> = ({
  review,
}) => {
  const driverMeta = (
    d: NonNullable<AwaitingReviewPayload["drivers"]>[number],
  ) => {
    const parts: string[] = [];
    if (d.dob) parts.push(`DOB ${d.dob}`);
    if (d.license_number) parts.push(`Lic # ${d.license_number}`);
    if (d.license_state) parts.push(d.license_state);
    return parts.join(" · ");
  };

  return (
    <div className="rql-iwf__review">
      <div className="rql-iwf__review-grid">
        {(
          [
            { label: "Insured", value: review.insured_name },
            { label: "State", value: review.state },
            { label: "Effective", value: review.effective_date },
            { label: "Policy type", value: review.policy_type },
          ] as { label: string; value?: string }[]
        ).map(({ label, value }) => (
          <div key={label} className="rql-iwf__review-cell">
            <span className="rql-iwf__review-cell-label">{label}</span>
            <span className="rql-iwf__review-cell-value">{value || "—"}</span>
          </div>
        ))}
        {review.application_id && (
          <div className="rql-iwf__review-cell rql-iwf__review-cell--wide">
            <span className="rql-iwf__review-cell-label">Application ID</span>
            <span className="rql-iwf__review-cell-value rql-iwf__review-cell-value--mono">
              {review.application_id}
            </span>
          </div>
        )}
      </div>

      {/* ── Data warnings banner — shown when any field is missing / invalid ── */}
      {review.data_warnings && review.data_warnings.length > 0 && (
        <div className="rql-iwf__warnings">
          <div className="rql-iwf__warnings-head">
            <FiAlertTriangle size={11} />
            {review.data_warnings.filter(w => w.severity === 'error').length > 0
              ? 'Action required before submitting'
              : 'Advisory — review before submitting'}
          </div>
          <ul className="rql-iwf__warnings-list">
            {review.data_warnings.map((w, i) => (
              <li key={i} className={`rql-iwf__warn-item rql-iwf__warn-item--${w.severity}`}>
                <span className="rql-iwf__warn-dot" />
                <span className="rql-iwf__warn-msg">{w.message}</span>
              </li>
            ))}
          </ul>
          <span className="rql-iwf__warn-hint">
            Tell me the correct values in the chat below — I'll update them before submitting.
          </span>
        </div>
      )}

      {review.drivers && review.drivers.length > 0 && (
        <div className="rql-iwf__review-section">
          <div className="rql-iwf__review-section-head">
            <FiUser size={11} /> Drivers ({review.drivers.length})
          </div>
          <ul className="rql-iwf__review-list">
            {review.drivers.map((d, i) => {
              const meta = driverMeta(d);
              const hasWarn = d.dob_invalid;
              return (
                <li key={i} className={hasWarn ? "rql-iwf__review-list-item--warn" : undefined}>
                  <span className="rql-iwf__review-list-name">
                    {d.name || `Driver ${i + 1}`}
                  </span>
                  {meta && (
                    <span className="rql-iwf__review-list-meta">{meta}</span>
                  )}
                  {d.dob_invalid && (
                    <span className="rql-iwf__dob-warn">
                      <FiAlertTriangle size={11} />
                      {d.dob ? `DOB ${d.dob} — invalid` : "DOB missing"} — provide correct date in chat
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {review.vehicles && review.vehicles.length > 0 && (
        <div className="rql-iwf__review-section">
          <div className="rql-iwf__review-section-head">
            <FiFileText size={11} /> Vehicles ({review.vehicles.length})
          </div>
          <ul className="rql-iwf__review-list">
            {review.vehicles.map((v, i) => {
              const meta: string[] = [];
              if (v.vin) meta.push(`VIN ${v.vin}`);
              if (v.usage) meta.push(v.usage);
              return (
                <li key={i}>
                  <span className="rql-iwf__review-list-name">
                    {[v.year, v.make, v.model].filter(Boolean).join(" ") ||
                      `Vehicle ${i + 1}`}
                  </span>
                  {meta.length > 0 && (
                    <span className="rql-iwf__review-list-meta">
                      {meta.join(" · ")}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {review.coverages && review.coverages.length > 0 && (
        <div className="rql-iwf__review-section">
          <div className="rql-iwf__review-section-head">
            <FiCheck size={11} /> Coverages ({review.coverages.length})
          </div>
          <dl className="rql-iwf__review-kv">
            {review.coverages.map((c) => (
              <React.Fragment key={c.key}>
                <dt>{COVERAGE_LABELS[c.key] || titlecase(c.key)}</dt>
                <dd>{c.value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}

      {review.carriers && review.carriers.length > 0 && (
        <div className="rql-iwf__review-section">
          <div className="rql-iwf__review-section-head">
            <FiZap size={11} /> Sending to ({review.carriers.length})
          </div>
          <ul className="rql-iwf__review-chips">
            {review.carriers.map((c, i) => (
              <li key={i} className="rql-iwf__review-chip">
                {c.name || `Carrier ${c.provider_id || i + 1}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ── Proposal document card — shown in chat after generation ── */
const PROPOSAL_STEPS = [
  { icon: "🔍", text: "Fetching quote details from NowCerts…" },
  { icon: "📊", text: "Analysing carrier coverages & limits…" },
  { icon: "🎨", text: "Building proposal layout…" },
  { icon: "✨", text: "Applying branding & design effects…" },
  { icon: "📄", text: "Generating PDF document…" },
  { icon: "🚀", text: "Finalising & uploading proposal…" },
];

const _AP_STEP_META: Record<string, { icon: string; progress: number }> = {
  analyse:  { icon: "🔍", progress: 20 },
  create:   { icon: "📊", progress: 45 },
  pdf:      { icon: "📄", progress: 72 },
  finalise: { icon: "✨", progress: 92 },
};

const ProposalGeneratingCard: React.FC<{
  carrierName: string;
  label?: string;
  stepLabel?: { step: string; label: string } | null;
}> = ({ carrierName, label, stepLabel }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [charIdx, setCharIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  // When backend sends real step labels, drive progress from those.
  // Otherwise fall back to internal cycling animation.
  const isLive = !!stepLabel;
  const liveTarget  = stepLabel?.label ?? "";
  const liveMeta    = stepLabel ? (_AP_STEP_META[stepLabel.step] ?? { icon: "⚙️", progress: 50 }) : null;

  // Internal cycling (fallback when no live step)
  useEffect(() => {
    if (isLive) return;
    const t = setInterval(() => {
      setStepIdx(i => {
        const next = (i + 1) % PROPOSAL_STEPS.length;
        setCharIdx(0);
        setDisplayText("");
        return next;
      });
    }, 2400);
    return () => clearInterval(t);
  }, [isLive]);

  // Typewriter — runs for both internal steps AND live backend labels
  const typeTarget = isLive ? liveTarget : PROPOSAL_STEPS[stepIdx].text;
  useEffect(() => {
    setDisplayText("");
    setCharIdx(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeTarget]);

  useEffect(() => {
    if (charIdx >= typeTarget.length) return;
    const t = setTimeout(() => {
      setDisplayText(typeTarget.slice(0, charIdx + 1));
      setCharIdx(c => c + 1);
    }, 22);
    return () => clearTimeout(t);
  }, [typeTarget, charIdx]);

  // Smooth progress bar — jump to live target or crawl internally
  useEffect(() => {
    if (isLive && liveMeta) {
      setProgress(liveMeta.progress);
      return;
    }
    setProgress(0);
    const t = setInterval(() => {
      setProgress(p => Math.min(p + 1.2, 95));
    }, 28);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, stepLabel?.step, stepIdx]);

  const step = isLive
    ? { icon: liveMeta?.icon ?? "⚙️", text: liveTarget }
    : PROPOSAL_STEPS[stepIdx];

  return (
    <div className="rql-pg-card">
      {/* Icon badge */}
      <div className="rql-pg-card__orb">
        <RiSparklingFill size={15} />
      </div>

      {/* Content */}
      <div className="rql-pg-card__content">
        <div className="rql-pg-card__header">
          <span className="rql-pg-card__title">{label ?? "Creating Quote Proposal"}</span>
          <span className="rql-pg-card__carrier">{carrierName}</span>
        </div>

        {/* Step text */}
        <div className="rql-pg-card__step">
          <span className="rql-pg-card__step-icon">{step.icon}</span>
          <span className="rql-pg-card__step-text">
            {displayText}
            <span className="rql-pg-card__cursor" />
          </span>
        </div>

        {/* Progress bar */}
        <div className="rql-pg-card__progress-track">
          <div
            className="rql-pg-card__progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Step dots */}
        <div className="rql-pg-card__dots">
          {PROPOSAL_STEPS.map((_, i) => (
            <span
              key={i}
              className={`rql-pg-card__dot${i === stepIdx ? " rql-pg-card__dot--active" : i < stepIdx ? " rql-pg-card__dot--done" : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ProposalDocCard: React.FC<{ carrierName: string; url: string }> = ({ carrierName, url }) => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {/* fallback: select text */ }
  };

  return (
    <div className="rql-proposal-doc-card">
      <div className="rql-proposal-doc-card__icon">
        <FiFileText size={22} />
      </div>
      <div className="rql-proposal-doc-card__body">
        <span className="rql-proposal-doc-card__title">{carrierName} — Quote Proposal</span>
        <span className="rql-proposal-doc-card__sub">Ready to share with client</span>
      </div>
      <div className="rql-proposal-doc-card__actions">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="rql-proposal-doc-card__btn rql-proposal-doc-card__btn--view"
        >
          ↗ View
        </a>
        <button
          type="button"
          className="rql-proposal-doc-card__btn rql-proposal-doc-card__btn--copy"
          onClick={copyLink}
        >
          {copied ? <><FiCheck size={11} /> Copied!</> : <><FiCopy size={11} /> Copy Link</>}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="rql-proposal-doc-card__btn rql-proposal-doc-card__btn--download"
          title="Open proposal — use browser Print → Save as PDF to download"
        >
          <FiDownload size={11} /> Download
        </a>
      </div>
    </div>
  );
};

/* ── Recommended Proposal Card — shown after auto_proposal SSE event ── */
const GoToRateBtn: React.FC<{ runId?: string }> = ({ runId }) => {
  const [loading, setLoading] = React.useState(false);
  if (!runId) return null;
  return (
    <button type="button" disabled={loading}
      className="rql-rec-proposal__btn"
      style={{ background: '#381574', color: '#fff', border: 'none', cursor: loading ? 'wait' : 'pointer' }}
      onClick={async () => {
        setLoading(true);
        try {
          const myampUrl = process.env.REACT_APP_MyAMP_Url || '';
          const token = localStorage.getItem('authToken') || '';
          const res = await fetch(`${myampUrl}/api/ams/rate-quoting/runs/${runId}/rate-url`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            window.open(data.rate_url, '_blank');
          }
        } catch { /* silently fail */ }
        finally { setLoading(false); }
      }}>
      <FiTrendingUp size={11} /> {loading ? 'Loading...' : 'Go to Rate'}
    </button>
  );
};

const RecommendedProposalCard: React.FC<{
  autoProposal:   NonNullable<import("./RateQuoteLinkContext").RunEntry["autoProposal"]>;
  onEmail?:       () => void;
  emailDrafting?: boolean;
  bindLink?:      string | null;
  runId?:         string;
}> = ({ autoProposal, onEmail, emailDrafting, bindLink, runId }) => {
  const url     = autoProposal.proposal_url!;
  const annual  = autoProposal.premium_annual ?? null;
  const monthly = annual ? Math.round(annual / 12) : null;

  return (
    <div className="rql-rec-proposal">
      {/* Header */}
      <div className="rql-rec-proposal__header">
        <span className="rql-rec-proposal__icon"><RiSparklingFill size={13} /></span>
        <div className="rql-rec-proposal__header-text">
          <span className="rql-rec-proposal__title">AI Recommended Proposal</span>
          <span className="rql-rec-proposal__carrier">{autoProposal.carrier}</span>
        </div>
        <span className="rql-rec-proposal__badge" aria-label="AI Recommended">
          <span className="rql-rec-proposal__badge-star">★</span>
          AI Pick
        </span>
      </div>

      {/* Why recommended */}
      {autoProposal.reason && (
        <div className="rql-rec-proposal__reason">
          <span className="rql-rec-proposal__reason-icon"><FiCheck size={10} /></span>
          {autoProposal.reason}
        </div>
      )}

      {/* Premium */}
      {annual != null && (
        <div className="rql-rec-proposal__pricing">
          <span className="rql-rec-proposal__annual">
            {fmt$(annual)}<span className="rql-rec-proposal__per"> /yr</span>
          </span>
          <span className="rql-rec-proposal__price-sep" />
          <span className="rql-rec-proposal__monthly">
            {fmt$(monthly)}<span className="rql-rec-proposal__per"> /mo</span>
          </span>
        </div>
      )}

      {/* Actions — View Proposal · Email Client · Bind Policy */}
      <div className="rql-rec-proposal__actions">
        <a href={url} target="_blank" rel="noopener noreferrer"
           className="rql-rec-proposal__btn rql-rec-proposal__btn--view">
          <FiFileText size={12} /> View Proposal
        </a>
        {onEmail && (
          <button
            type="button"
            className={`rql-rec-proposal__btn${emailDrafting ? " rql-rec-proposal__btn--loading" : ""}`}
            disabled={!!emailDrafting}
            onClick={onEmail}
          >
            {emailDrafting
              ? <><span className="rql-icq__spinner" /> Drafting…</>
              : <><FiSend size={11} /> Email Client</>}
          </button>
        )}
        {bindLink ? (
          <a href={bindLink} target="_blank" rel="noopener noreferrer"
             className="rql-rec-proposal__btn rql-rec-proposal__btn--bind">
            <FiTrendingUp size={11} /> Bind Policy
          </a>
        ) : (
          <button type="button" className="rql-rec-proposal__btn rql-rec-proposal__btn--bind">
            <FiTrendingUp size={11} /> Bind Policy
          </button>
        )}
        <GoToRateBtn runId={runId} />
      </div>
    </div>
  );
};

/* ── Quote Summary Card — shown after quote summary PDF is generated ── */
const QuoteSummaryCard: React.FC<{
  summary: NonNullable<import("./RateQuoteLinkContext").RunEntry["quoteSummary"]>;
  onEmailClient?: () => void;
  emailDrafting?: boolean;
}> = ({ summary, onEmailClient, emailDrafting }) => {
  const annual  = summary.best_premium ?? null;
  const monthly = annual ? Math.round(annual / 12) : null;

  return (
    <div className="rql-quote-summary">
      {/* Header */}
      <div className="rql-quote-summary__header">
        <span className="rql-quote-summary__icon"><FiFileText size={15} /></span>
        <div className="rql-quote-summary__header-text">
          <span className="rql-quote-summary__title">Quote Summary</span>
          <span className="rql-quote-summary__sub">
            {summary.carrier_count} carrier{summary.carrier_count !== 1 ? "s" : ""} quoted
            {summary.insured_name ? ` · ${summary.insured_name}` : ""}
          </span>
        </div>
      </div>

      {/* Best carrier highlight */}
      {summary.best_carrier && annual != null && (
        <div className="rql-quote-summary__best">
          <span className="rql-quote-summary__best-label">Best Rate</span>
          <span className="rql-quote-summary__best-carrier">{summary.best_carrier}</span>
          <span className="rql-quote-summary__best-price">
            {fmt$(annual)}<span className="rql-quote-summary__per"> /yr</span>
            {monthly != null && (
              <>
                <span className="rql-quote-summary__price-sep" />
                {fmt$(monthly)}<span className="rql-quote-summary__per"> /mo</span>
              </>
            )}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="rql-quote-summary__actions">
        <a
          href={summary.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="rql-quote-summary__btn rql-quote-summary__btn--view"
        >
          <FiFileText size={12} /> View PDF
        </a>
        <a
          href={summary.file_url}
          download={summary.file_name}
          className="rql-quote-summary__btn rql-quote-summary__btn--download"
        >
          <FiDownload size={12} /> Download
        </a>
        {onEmailClient && (
          <button
            type="button"
            className={`rql-quote-summary__btn rql-quote-summary__btn--email${emailDrafting ? " rql-quote-summary__btn--loading" : ""}`}
            disabled={!!emailDrafting}
            onClick={onEmailClient}
          >
            {emailDrafting
              ? <><span className="rql-icq__spinner" /> Drafting…</>
              : <><FiSend size={11} /> Email Client</>}
          </button>
        )}
      </div>
    </div>
  );
};

/** Maps common carrier decline reason keywords to an actionable fix hint. */
function getFixSuggestion(reason: string | undefined): string | null {
  if (!reason) return null;
  const r = reason.toLowerCase();
  if (r.includes('mileage') || r.includes('annual miles'))
    return 'Try reducing annual mileage and re-quote.';
  // HO-specific: roof age, dwelling age, property age — must check BEFORE generic "age"
  if (r.includes('roof age') || r.includes('age of roof') || r.includes('roof is greater'))
    return 'Roof age exceeds carrier guidelines — check roof year built or update roofing details.';
  if (r.includes('dwelling') || r.includes('year built') || r.includes('property age'))
    return 'Property age or year built may be outside carrier limits — verify property details.';
  if (r.includes('replacement cost') || r.includes('replacement cost protection'))
    return 'Replacement cost endorsement missing — add replacement cost coverage and re-quote.';
  // PAP-specific: driver age
  if ((r.includes('age') && (r.includes('driver') || r.includes('minor') || r.includes('young'))) || r.includes('young driver'))
    return 'Driver age may be outside underwriting guidelines — check driver DOB.';
  if (r.includes('loss') || r.includes('claim') || r.includes('accident'))
    return 'Prior loss history may exceed the carrier\'s limit — review the 5-year loss history.';
  if (r.includes('license') || r.includes('mvr') || r.includes('violation') || r.includes('dui') || r.includes('dwi'))
    return 'MVR violations or license suspension detected — verify driver license status.';
  if (r.includes('vin') || r.includes('vehicle') || r.includes('model year'))
    return 'Vehicle year or VIN may be outside acceptable range — verify vehicle details.';
  if (r.includes('coverage') || r.includes('limit') || r.includes('exceed'))
    return 'Requested coverage limits may exceed the carrier\'s maximum — try lower limits.';
  if (r.includes('state') || r.includes('appointment') || r.includes('not licensed'))
    return 'This carrier may not be appointed in this state — check agency appointments.';
  if (r.includes('prior insurance') || r.includes('lapse') || r.includes('no prior'))
    return 'Prior insurance lapse detected — confirm continuous coverage history with the client.';
  if (r.includes('credit') || r.includes('score'))
    return 'Credit score may be below the carrier\'s threshold — check client consent and score.';
  if (r.includes('not eligible') || r.includes('not available') || r.includes('not enough info'))
    return 'Carrier could not process this submission — review all required fields and re-quote.';
  return null;
}

interface InlineChatQuotesProps {
  quotes:                 QuoteResult[];
  onBack:                 () => void;
  backendRunId:           string | null;
  recommendation?:        import("./RateQuoteLinkContext").RunEntry["recommendation"];
  onDraftEmail:           (q: QuoteResult) => void;
  onRequoteFailed?:       (q: QuoteResult) => void;
  emailDrafting?:         boolean;
  insuredDatabaseId?:     string;
  insuredName?:           string;
  onProposalGenerated?:   (q: QuoteResult, url: string) => void;
  onProposalGenerating?:  (q: QuoteResult) => void;
  /** Pre-populated proposal URLs (e.g. auto-generated for the best quote). */
  externalProposalUrls?:  Record<string, string>;
  /** For renewal runs: the current active policy's annual premium for delta display. */
  currentPremium?:        number | null;
  /** Whether this is a renewal run. */
  isRenewal?:             boolean;
}

const InlineChatQuotes: React.FC<InlineChatQuotesProps> = ({
  quotes, onBack, backendRunId, recommendation, onDraftEmail, onRequoteFailed, emailDrafting,
  insuredDatabaseId, insuredName, onProposalGenerated, onProposalGenerating,
  externalProposalUrls, currentPremium, isRenewal,
}) => {
  const [busyQuoteId, setBusyQuoteId] = useState<string | null>(null);
  const [proposalUrls, setProposalUrls] = useState<Record<string, string>>({});
  // Merge auto-generated URLs (from parent) with manually generated ones (local state)
  const allProposalUrls = { ...externalProposalUrls, ...proposalUrls };

  const quoted = quotes
    .filter((q) => q.premiumAmount != null && Number(q.premiumAmount) > 0)
    .sort((a, b) => Number(a.premiumAmount) - Number(b.premiumAmount));
  const failed = quotes.filter(
    (q) => !q.premiumAmount || Number(q.premiumAmount) <= 0,
  );
  const lowest = quoted[0]?.premiumAmount ?? null;
  const savings =
    quoted.length > 1
      ? Number(quoted[quoted.length - 1].premiumAmount) - Number(lowest)
      : null;

  const bestId = recommendation?.best_quote_id ?? null;
  const rankMap = Object.fromEntries(
    (recommendation?.ranking ?? []).map(r => [r.quote_id, {
      ...r,
      // backend sends `reason` (string) for rich text; `reasons[0]` as legacy fallback
      reason: r.reason || "",
    }]),
  );

  const quoteKey = (q: QuoteResult) => q.quoteId || q.carrierId || "";
  // Prefer the NowCerts GUID (written back after AMS persistence).
  // fetch_quote_details on the backend requires a valid UUID, not the rater's numeric ID.
  const quoteDbId = (q: QuoteResult) => q.quoteDatabaseId || quoteKey(q);

  const handleProposal = async (q: QuoteResult) => {
    const qk    = quoteKey(q);
    const qdbId = quoteDbId(q);
    if (!backendRunId || !qk) return;
    setBusyQuoteId(`proposal-${qk}`);
    onProposalGenerating?.(q);
    try {
      const authToken = localStorage.getItem("authToken") || "";
      const ncToken   = localStorage.getItem("amsToken")   || "";
      const resp = await fetch(
        `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/runs/${backendRunId}/proposals`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            quote_id: qdbId,
            carrier:  q.carrierName,
            nc_token: ncToken,
            ...(insuredDatabaseId ? { insured_database_id: insuredDatabaseId } : {}),
            ...(insuredName       ? { insured_name: insuredName }               : {}),
          }),
        },
      );
      if (resp.ok) {
        const data = await resp.json();
        const url: string | undefined = data?.proposal_url;
        if (url) {
          setProposalUrls(prev => ({ ...prev, [qk]: url }));
          onProposalGenerated?.(q, url);
        }
      }
    } catch {/* silent */} finally {
      setBusyQuoteId(null);
    }
  };

  // Delegate to parent — parent sends quote details to AI chat and lets the
  // AI generate a rich draft (subject, body, proposal link).
  const handleDraftEmail = (q: QuoteResult) => {
    onDraftEmail(q);
  };

  return (
    <div className="rql-chat__msg rql-chat__msg--ai rql-icq-row">
      <span className="rql-chat__avatar rql-chat__avatar--top">
        <RiSparklingFill size={11} />
      </span>
      <div className="rql-icq">
        <div className="rql-icq__header">
          <span className="rql-icq__header-icon">
            <FiZap size={13} />
          </span>
          <div>
            <div className="rql-icq__title">
              {quoted.length > 0
                ? `${quoted.length} quote${quoted.length !== 1 ? "s" : ""} received!`
                : "Submission complete"}
            </div>
            {savings != null && savings > 0 && (
              <div className="rql-icq__sub">
                Save up to {fmt$(savings)} vs. highest premium
              </div>
            )}
          </div>
        </div>

        {/* Renewal delta banner — shows current premium vs best new quote */}
        {isRenewal && currentPremium != null && lowest != null && (
          <div className="rql-icq__renewal-delta">
            <span className="rql-icq__renewal-delta__label">Renewal comparison</span>
            <div className="rql-icq__renewal-delta__row">
              <span className="rql-icq__renewal-delta__item">
                <span className="rql-icq__renewal-delta__key">Current premium</span>
                <span className="rql-icq__renewal-delta__val">${currentPremium.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr</span>
              </span>
              <span className="rql-icq__renewal-delta__arrow">→</span>
              <span className="rql-icq__renewal-delta__item">
                <span className="rql-icq__renewal-delta__key">Best new quote</span>
                <span className="rql-icq__renewal-delta__val">${Number(lowest).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/yr</span>
              </span>
              <span className={`rql-icq__renewal-delta__badge ${Number(lowest) < currentPremium ? 'rql-icq__renewal-delta__badge--save' : 'rql-icq__renewal-delta__badge--up'}`}>
                {Number(lowest) < currentPremium
                  ? `Save $${(currentPremium - Number(lowest)).toLocaleString(undefined, { maximumFractionDigits: 0 })} (${Math.round((1 - Number(lowest) / currentPremium) * 100)}%)`
                  : `+$${(Number(lowest) - currentPremium).toLocaleString(undefined, { maximumFractionDigits: 0 })} (${Math.round((Number(lowest) / currentPremium - 1) * 100)}% increase)`}
              </span>
            </div>
          </div>
        )}

        {quoted.length > 0 && (
          <div className="rql-icq__cards">
            {quoted.map((q, i) => {
              const termMonths = Number(q.contractTerm) || 12;
              const perTerm   = Number(q.premiumAmount);
              const monthly   = Math.round(perTerm / termMonths);
              const termLabel = termMonths === 12 ? 'yr' : `${termMonths} mo`;
              const qk      = quoteKey(q);
              const isRec   = bestId != null && qk === bestId;
              // Fallback to lowest-price badge when no recommendation is available
              const isBest  = isRec || (bestId == null && q.premiumAmount === lowest);
              const initials = (q.carrierName ?? "?").charAt(0).toUpperCase();
              const rankInfo = rankMap[qk];
              return (
                <div
                  key={qk || i}
                  className={`rql-icq__card${isBest ? " rql-icq__card--best" : ""}`}
                >
                  <div className="rql-icq__card-head">
                    <div className="rql-icq__card-ident">
                      {q.carrierLogoURL ? (
                        <div className="rql-icq__logo-wrap">
                          <img src={q.carrierLogoURL} className="rql-icq__logo" alt={q.carrierName ?? ""} />
                        </div>
                      ) : (
                        <span className="rql-icq__badge">{initials}</span>
                      )}
                      <div className="rql-icq__card-meta">
                        <span className="rql-icq__carrier">{q.carrierName || q.carrierId || "Unknown"}</span>
                        {q.quoteType && <span className="rql-icq__type">{q.quoteType}</span>}
                      </div>
                    </div>
                    {isBest && (
                      <span className="rql-icq__best">
                        <FiCheck size={9} /> {isRec ? "Recommended" : "Best Price"}
                      </span>
                    )}
                  </div>

                  {rankInfo?.reason && (
                    <div className="rql-icq__rec-reason">
                      <span className="rql-icq__rec-reason-icon"><FiCheck size={11} /></span>
                      {rankInfo.reason}
                    </div>
                  )}

                  <div className="rql-icq__pricing">
                    <div className="rql-icq__price-main">
                      <span className="rql-icq__amount">{fmt$(perTerm)}</span>
                      <span className="rql-icq__per">/ {termLabel}</span>
                    </div>
                    <div className="rql-icq__price-sep" />
                    <div className="rql-icq__price-sub">
                      <div className="rql-icq__price-sub-row">
                        <span className="rql-icq__monthly">{fmt$(monthly)}</span>
                        <span className="rql-icq__per">/ mo</span>
                      </div>
                      {termMonths < 12 && (
                        <div className="rql-icq__term-inline">{termMonths}-month term</div>
                      )}
                    </div>
                  </div>
                  {q.coverages && q.coverages.length > 0 && (
                    <dl className="rql-icq__coverages">
                      {q.coverages.map((c: any, ci: number) => (
                        <React.Fragment key={ci}>
                          <dt>{c.coverageName || c.coverageCode || `Coverage ${ci + 1}`}</dt>
                          <dd>
                            {[c.limit, c.deductible].filter(Boolean).join(" / ") || "Included"}
                            {c.premium != null && <span className="rql-icq__cov-prem"> · {fmt$(c.premium)}</span>}
                          </dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  )}

                  {/* Prominent proposal banner — only for the recommended card once auto-proposal URL is ready */}
                  {isRec && allProposalUrls[qk] && (
                    <div className="rql-icq__auto-proposal">
                      <span className="rql-icq__auto-proposal-icon">
                        <RiSparklingFill size={13} />
                      </span>
                      <div className="rql-icq__auto-proposal-text">
                        <span className="rql-icq__auto-proposal-label">Proposal prepared by AI</span>
                        <span className="rql-icq__auto-proposal-sub">Ready to review &amp; share with client</span>
                      </div>
                      <a
                        href={allProposalUrls[qk]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rql-icq__auto-proposal-link"
                      >
                        <FiFileText size={11} /> View Proposal
                      </a>
                    </div>
                  )}

                  <div className="rql-icq__actions">
                    {q.carrierBridgingLink ? (
                      <a href={q.carrierBridgingLink} target="_blank" rel="noopener noreferrer" className="rql-icq__btn rql-icq__btn--bind">
                        <FiTrendingUp size={11} /> Bind Policy
                      </a>
                    ) : (
                      <button type="button" className="rql-icq__btn rql-icq__btn--bind">
                        <FiTrendingUp size={11} /> Bind Policy
                      </button>
                    )}
                    {allProposalUrls[qk] ? (
                      <a
                        href={allProposalUrls[qk]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rql-icq__btn rql-icq__btn--proposal-ready"
                      >
                        <FiFileText size={11} /> View Proposal
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="rql-icq__btn"
                        disabled={busyQuoteId === `proposal-${qk}`}
                        onClick={() => handleProposal(q)}
                      >
                        <FiFileText size={11} />
                        {busyQuoteId === `proposal-${qk}` ? "Generating…" : "Proposal"}
                      </button>
                    )}
                    <button
                      type="button"
                      className={`rql-icq__btn${emailDrafting ? " rql-icq__btn--loading" : ""}`}
                      disabled={!!emailDrafting}
                      onClick={() => handleDraftEmail(q)}
                    >
                      {emailDrafting
                        ? <><span className="rql-icq__spinner" /> Drafting…</>
                        : <><FiSend size={11} /> Email Client</>
                      }
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {failed.length > 0 && (
          <div className="rql-icq__failed">
            <span className="rql-icq__failed-label">
              <FiX size={10} /> {failed.length} carrier{failed.length !== 1 ? "s" : ""} did not return a premium
            </span>
            <div className="rql-icq__failed-list">
              {failed.map((q, i) => (
                <div key={i} className="rql-icq__failed-row">
                  {q.carrierLogoURL ? (
                    <img src={q.carrierLogoURL} className="rql-icq__failed-logo" alt={q.carrierName ?? ""} />
                  ) : (
                    <span className="rql-icq__failed-badge">
                      {(q.carrierName ?? q.carrierId ?? "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="rql-icq__failed-info">
                    <span className="rql-icq__failed-name">{q.carrierName || q.carrierId || "Unknown"}</span>
                    {q.failureReason && <span className="rql-icq__failed-reason">{q.failureReason}</span>}
                    {getFixSuggestion(q.failureReason) && (
                      <span className="rql-icq__failed-fix">
                        <FiAlertTriangle size={9} /> {getFixSuggestion(q.failureReason)}
                      </span>
                    )}
                  </div>
                  {/* Fix & requote — hidden for now */}
                </div>
              ))}
            </div>
          </div>
        )}

        {quotes.length === 0 && (
          <div className="rql-icq__empty">
            <FiAlertTriangle size={14} />
            No carrier premiums were returned for this submission.
          </div>
        )}

        <div className="rql-icq__footer">
          <button type="button" className="rql-iwf__cancel" onClick={onBack}>
            <FiChevronLeft size={11} /> Back to results
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Email draft card — compose window, appears in chat flow ── */
const EmailDraftCard: React.FC<{
  draft:        { toAddr?: string; subject: string; body: string };
  backendRunId: string | null;
  onClose:      () => void;
  onSent:       () => void;
}> = ({ draft, backendRunId, onClose, onSent }) => {
  const [toAddr,  setToAddr]  = useState(draft.toAddr ?? "");
  const [subject, setSubject] = useState(draft.subject);
  const [body,    setBody]    = useState(draft.body);
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState("");

  // Keep fields in sync if the parent re-generates the draft.
  React.useEffect(() => {
    setSubject(draft.subject);
    setBody(draft.body);
  }, [draft.subject, draft.body]);

  const send = async () => {
    if (sending || !toAddr.trim() || !backendRunId) return;
    setError("");
    setSending(true);
    try {
      const authToken = localStorage.getItem("authToken") || "";
      const ncToken   = localStorage.getItem("amsToken")  || "";
      const resp = await fetch(
        `${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/runs/${backendRunId}/send-email`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ to: toAddr.trim(), subject, body, nc_token: ncToken }),
        },
      );
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        setSent(true);
        setTimeout(onSent, 1800);
      } else {
        setError(data.errorMessage || "Send failed — please try again.");
      }
    } catch {
      setError("Connection error — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rql-chat__msg rql-chat__msg--ai rql-email-draft-row">
      <span className="rql-chat__avatar rql-chat__avatar--top">
        <RiSparklingFill size={11} />
      </span>
      <div className="rql-email-draft">

        {/* Header */}
        <div className="rql-email-draft__header">
          <FiSend size={12} />
          <span className="rql-email-draft__title">Email Draft</span>
          <button type="button" className="rql-email-draft__close" onClick={onClose} title="Dismiss">
            <FiX size={11} />
          </button>
        </div>

        {sent ? (
          <div className="rql-email-draft__sent">
            <FiCheck size={16} />
            <span>Email sent to {toAddr}</span>
          </div>
        ) : (
          <>
            {/* To / Subject */}
            <div className="rql-email-draft__fields">
              <label className="rql-email-draft__label">
                <span className="rql-email-draft__label-text">To</span>
                <input
                  className="rql-email-draft__input"
                  value={toAddr}
                  onChange={e => setToAddr(e.target.value)}
                  placeholder="client@example.com"
                  autoFocus
                />
              </label>
              <label className="rql-email-draft__label">
                <span className="rql-email-draft__label-text">Subject</span>
                <input
                  className="rql-email-draft__input"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                />
              </label>
            </div>

            {/* Body */}
            <div className="rql-email-draft__body-area">
              <textarea
                className="rql-email-draft__textarea"
                rows={14}
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Email body…"
              />
            </div>

            {/* Error */}
            {error && <div className="rql-email-draft__error">{error}</div>}

            {/* Footer */}
            <div className="rql-email-draft__footer">
              <button type="button" className="rql-email-draft__cancel-send" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="rql-email-draft__send"
                disabled={sending || !toAddr.trim()}
                title={!toAddr.trim() ? "Enter recipient email first" : undefined}
                onClick={send}
              >
                {sending ? "Sending…" : <><FiSend size={11} /> Send email</>}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

function buildDynamicSuggestions(review: AwaitingReviewPayload | null): string[] {
  const base = ['Submit to carriers', 'Change effective date'];
  if (!review?.coverages) return base;
  const keys = review.coverages.map(c => c.key);
  const extra: string[] = [];
  if (keys.includes('bodily_injury_per_person')) extra.push('Change BI to 100/300');
  if (keys.includes('collision_deductible')) extra.push('Set collision deductible to $500');
  if (keys.includes('comprehensive_deductible')) extra.push('Set comp deductible to $500');
  if (!keys.includes('rental_reimbursement')) extra.push('Add rental reimbursement');
  if (keys.includes('pip') || keys.includes('personal_injury_protection')) extra.push('Remove PIP');
  if (keys.includes('roadside_assistance') || !keys.includes('roadside_assistance')) extra.push('Add roadside assistance');
  // Add first coverage change suggestion dynamically
  const changeable = keys.filter(k => !['prior_insurance','residence_type','prior_carrier_id','prior_liability_limit','prior_months_coverage'].includes(k));
  if (changeable.length > 0 && extra.length < 4) {
    const k = changeable[0];
    const label = k.replace(/_/g,' ');
    extra.push(`Change ${label}`);
  }
  return [...base, ...extra].slice(0, 7);
}

interface ChangeItem {
  field: string;
  from: string;
  to: string;
}

function computeChanges(
  original: AwaitingReviewPayload,
  updated: AwaitingReviewPayload,
): ChangeItem[] {
  const changes: ChangeItem[] = [];

  if (
    original.effective_date &&
    updated.effective_date &&
    original.effective_date !== updated.effective_date
  ) {
    changes.push({
      field: "Effective date",
      from: original.effective_date,
      to: updated.effective_date,
    });
  }

  const origMap: Record<string, string> = {};
  for (const c of original.coverages ?? []) origMap[c.key] = c.value;
  const updMap: Record<string, string> = {};
  for (const c of updated.coverages ?? []) updMap[c.key] = c.value;

  for (const [key, val] of Object.entries(updMap)) {
    if (val === "removed") continue;
    if (origMap[key] !== val) {
      changes.push({
        field: COVERAGE_LABELS[key] || titlecase(key),
        from: origMap[key] ?? "—",
        to: val,
      });
    }
  }
  for (const [key, val] of Object.entries(origMap)) {
    if (updMap[key] === "removed" || (!(key in updMap) && val !== "removed")) {
      if (updMap[key] === "removed") {
        changes.push({
          field: COVERAGE_LABELS[key] || titlecase(key),
          from: val,
          to: "Removed",
        });
      }
    }
  }

  const origCarriers = new Set(
    (original.carriers ?? []).map((c) => String(c.provider_id)),
  );
  const updCarriers = new Set(
    (updated.carriers ?? []).map((c) => String(c.provider_id)),
  );
  for (const c of original.carriers ?? []) {
    if (!updCarriers.has(String(c.provider_id))) {
      changes.push({ field: "Carrier removed", from: c.name ?? "", to: "—" });
    }
  }
  for (const c of updated.carriers ?? []) {
    if (!origCarriers.has(String(c.provider_id))) {
      changes.push({ field: "Carrier added", from: "—", to: c.name ?? "" });
    }
  }

  return changes;
}

interface RequoteMods {
  coverages?: Array<{ key: string; value: string }>;
  effective_date?: string | null;
}

const InlineWorkflowPanel: React.FC<{
  onBack: () => void;
  onRequote?: (opts: { rowId: string; workflow: 'new' | 'renewal'; label: string; insuredName: string; insuredId?: string }) => void;
  initialMessage?: string;
}> = ({ onBack, onRequote, initialMessage }) => {
  const { activeRun, submitStepInput, history, getBackendId, renewals, prospects } =
    useRateQuoteLink();

  // For prospect-triggered runs, QuoteRow.id == NowCerts insuredDatabaseId.
  // Used as fallback when the run record has no insured_id stored.
  const activeRowId       = activeRun?.rowId ?? null;
  const activeRow         = activeRowId
    ? ([...(renewals ?? []), ...(prospects ?? [])].find(r => r.id === activeRowId) ?? null)
    : null;
  const insuredDatabaseId = activeRow?.id ?? activeRowId ?? "";
  const insuredName       = activeRow?.insured ?? "";
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setIsTransitioning(false), 400);
    return () => clearTimeout(t);
  }, []);

  const [chatInput, setChatInput] = useState("");
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const [reviewOverride, setReviewOverride] =
    useState<AwaitingReviewPayload | null>(null);
  const reviewOverridesRef = useRef<AwaitingReviewPayload | null>(null);
  const originalReviewRef = useRef<AwaitingReviewPayload | null>(null);
  const [aiSuggestsConfirm, setAiSuggestsConfirm] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Array<{key: string; current: string; recommended: string; reason: string}>>([]);
  const [aiSuggestsRequote, setAiSuggestsRequote] = useState(false);
  const [requoteMods, setRequoteMods] = useState<RequoteMods | null>(null);
  // Email draft card state
  const [emailDraft, setEmailDraft] = useState<{
    toAddr?: string; subject: string; body: string;
  } | null>(null);
  const [emailLoading, setEmailLoading] = useState(false);
  // Holds the quoteId while waiting for AI to return email content
  const _pendingEmailQuoteRef = useRef<{ quoteId: string } | null>(null);
  // Tracks generated proposal URLs by quoteKey so email draft can include the real link
  const [proposalUrlsByQuote, setProposalUrlsByQuote] = useState<Record<string, string>>({});
  // Proposal cards rendered in chat after generation
  const [proposalCards, setProposalCards] = useState<Array<{ carrier: string; url: string; quoteKey: string }>>([]);
  // quoteKey currently generating (shows loading bubble)
  const [proposalGenerating, setProposalGenerating] = useState<string | null>(null);

  // Unified chat hook — initialised early so the dependency arrays below can reference it.
  // runIdRef is initialised with activeRun?.id; the imperative update below sets it on
  // subsequent renders before the hook reads .current.
  const _earlyRunIdRef = useRef<string | undefined>(activeRun?.id);
  if (!_earlyRunIdRef.current && activeRun?.id) _earlyRunIdRef.current = activeRun.id;
  const runChat = useRunChat(_earlyRunIdRef.current ? getBackendId(_earlyRunIdRef.current) : null);

  // Auto-send the caller-provided initial message once (e.g. "Email Client" click)
  const initialMsgSentRef = useRef(false);
  useEffect(() => {
    if (!initialMessage || initialMsgSentRef.current) return;
    initialMsgSentRef.current = true;
    // Slight delay so the chat UI has painted before we send
    const t = setTimeout(() => { handleSend(initialMessage); }, 600);
    return () => clearTimeout(t);
  }, [initialMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const runIdRef = _earlyRunIdRef;  // alias so the rest of the component still reads runIdRef

  const run =
    (runIdRef.current
      ? history.find((h) => h.id === runIdRef.current)
      : undefined) ?? activeRun;

  // PLRChatView handles all chat history via chat_events in MongoDB.
  // No old runChat seed/restore/chat-history needed.

  // ── React to backend-streamed auto_proposal ───────────────────────────────
  // When the SSE stream delivers scope:'auto_proposal', RateQuoteLinkContext
  // sets run.autoProposal. We react here: register the proposal card and
  // append an AI chat message. No frontend API call — backend does all the work.
  const autoProposalSeenRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const ap = run?.autoProposal;
    if (!ap?.proposal_url) return;
    // Guard: only handle each unique proposal URL once
    if (autoProposalSeenRef.current === ap.proposal_url) return;
    autoProposalSeenRef.current = ap.proposal_url;

    const bestId = ap.quote_id || ap.quote_db_id || "";
    const bestQuote = (run?.quotes ?? []).find(q =>
      (q.quoteId || q.carrierId) === bestId ||
      q.quoteDatabaseId === bestId
    ) ?? null;

    if (bestQuote) {
      handleProposalGenerated(bestQuote, ap.proposal_url);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.autoProposal?.proposal_url]);

  // ── Timeout fallback: reveal quotes if auto-proposal hasn't arrived after 3 min ─
  // Step 4 (s4) now generates the proposal inline in the SSE stream, so this
  // timeout is only a safety net for SSE connection drops. 180s matches the
  // stream's own safety cap.

  // Pre-compute these before the early return so handleSend (a hook) can reference them.
  const awaitingStep = run?.steps.find((s) => s.status === "awaiting_input") ?? null;
  const review = awaitingStep?.awaitingInput?.review ?? null;

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [runChat.messages, runChat.thinking, awaitingStep, run?.status]);

  // Unified send handler — covers review-gate, completed-run, and field-input modes.
  // Declared before the `if (!run) return null` guard so hook call order is stable.
  const handleSend = useCallback(async (
    text: string,
    confirmOpts?: ConfirmOpts,
  ): Promise<Record<string, any> | null> => {
    if (!text.trim() || runChat.thinking) return null;
    setChatInput("");

    // ── Field-input mode (non-review pause): submit directly, no AI chat ─────
    if (awaitingStep?.awaitingInput && !review && !confirmOpts) {
      const fields = awaitingStep.awaitingInput.fields ?? [];
      const _fk = (f: (typeof fields)[number]): string => f.key ?? f.name ?? '';
      const submitValues: Record<string, string> =
        fields.length === 1
          ? { [_fk(fields[0])]: text.trim() }
          : fields.length === 0
          ? { reply: text.trim() }
          : { [_fk(fields[0])]: text.trim() };
      await runChat.localTransact(
        text.trim(),
        async () => {
          await submitStepInput(run!.id, awaitingStep.id, submitValues);
          setSubmitted(true);
        },
        "Got it — continuing the workflow…",
        "Couldn't submit that — please try again.",
      );
      return null;
    }

    // ── AI chat (review gate or completed run) ────────────────────────────────
    const data = await runChat.send(text, confirmOpts);
    if (!data) return null;

    if (Array.isArray(data.suggestions) && data.suggestions.length > 0 && runChat.messages.length <= 4) {
      setAiSuggestions(data.suggestions);
    }
    if (data.updated_review) {
      setReviewOverride(data.updated_review);
      reviewOverridesRef.current = data.updated_review;
    }
    if (data.action === "confirm") setAiSuggestsConfirm(true);
    if (data.action === "cancel")  onBack();
    if (data.action === "requote" && data.requote_mods) {
      setAiSuggestsRequote(true);
      setRequoteMods(data.requote_mods);
    }
    // AI returned a ready-to-edit email draft — open the card pre-filled.
    // Handles all three paths: legacy email_draft field, standard pending_confirmation
    // (same as Email Client button), and plain-text reply fallback.
    if (data.email_draft) {
      const d = data.email_draft;
      setEmailDraft({ toAddr: d.to_addr ?? "", subject: d.subject ?? "", body: d.body ?? "" });
      _pendingEmailQuoteRef.current = null;
    } else if (
      (data.action === "draft_email" || data.pending_confirmation?.action_type === "draft_email") &&
      data.pending_confirmation?.params
    ) {
      // Standard path: same response shape as clicking "Email Client" button
      const p = data.pending_confirmation.params;
      setEmailDraft({ toAddr: p.to_addr ?? "", subject: p.subject ?? "", body: p.body ?? "" });
      _pendingEmailQuoteRef.current = null;
    } else if (_pendingEmailQuoteRef.current && data.reply) {
      const parsed = parseEmailFromReply(data.reply);
      if (parsed) {
        setEmailDraft({ subject: parsed.subject, body: parsed.body });
        _pendingEmailQuoteRef.current = null;
      }
    }
    return data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runChat, awaitingStep, review, submitStepInput, onBack]);

  if (!run) return null;

  const isPaused = !!awaitingStep;

  if (review && !originalReviewRef.current) {
    originalReviewRef.current = review;
  }
  const isCompleted = run.status === "completed";
  const isFailed    = run.status === "failed";

  // s4 ("Generate Proposal") step status — used to gate the reveal
  const s4Step = run.steps.find(s => s.id === 's4' || s.id === 'r4');
  const s4Done = s4Step?.status === 'completed' || s4Step?.status === 'failed';


  // Show quotes as soon as the run completes; proposal card appears when s4 finishes.
  const showResults = isCompleted && (run.quotes !== undefined);

  const isSubmittingNow = (submitted || submitting) && !isCompleted && !isFailed;
  const completedCt = run.steps.filter((s) => s.status === "completed").length;
  const total = run.steps.length;

  const onSubmit = async () => {
    if (!awaitingStep || submitting) return;
    setSubmitting(true);
    try {
      const inputs: Record<string, any> = { confirmed: "yes" };
      if (reviewOverridesRef.current) {
        inputs.review_overrides = reviewOverridesRef.current;
      }
      await submitStepInput(run.id, awaitingStep.id, inputs);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequote = () => {
    if (!run.rowId || !run.workflow || !onRequote) return;
    const insuredName = run.label.replace(/^(New|Renewal)\s*[—-]\s*/i, "").trim();
    const insuredId   = activeRow?.insuredId;
    onRequote({ rowId: run.rowId, workflow: run.workflow, label: run.label, insuredName, insuredId });
  };

  // Parses Subject + body from a plain-text AI reply containing an email draft.
  const parseEmailFromReply = (reply: string): { subject: string; body: string } | null => {
    const subjectMatch = reply.match(/^Subject:\s*(.+)$/im);
    if (!subjectMatch) return null;
    const subject = subjectMatch[1].trim();
    const subjectIndex = reply.indexOf(subjectMatch[0]);
    const body = reply.slice(subjectIndex + subjectMatch[0].length).replace(/^\s*\n/, "").trim();
    return { subject, body };
  };

  // Fetches email draft content directly from the backend WITHOUT adding any
  // chat bubbles. Shows EmailDraftCard immediately with pre-filled fields.
  const handleAiDraftEmail = async (q: QuoteResult) => {
    const backendId = _earlyRunIdRef.current ? getBackendId(_earlyRunIdRef.current) : null;
    if (!backendId || emailLoading) return;

    const insuredName  = run.label.replace(/^(New|Renewal)\s*[—-]\s*/i, "").trim();
    const _eTerm       = Number(q.contractTerm) || 12;
    const _eTermLabel  = _eTerm === 12 ? 'yr' : `${_eTerm}mo`;
    const premium      = fmt$(Number(q.premiumAmount));
    const monthly      = fmt$(Math.round(Number(q.premiumAmount) / _eTerm));
    const quoteRef     = q.quoteId || q.carrierId || "";
    const proposalUrl  = proposalUrlsByQuote[quoteRef] || null;
    const message      =
      `Draft a professional client email for the ${q.carrierName ?? "carrier"} quote for ${insuredName}. ` +
      `Premium: ${premium}/${_eTermLabel} (${monthly}/mo). ` +
      `Include: a compelling subject line, personalised intro, key coverage highlights, and a friendly closing. ` +
      (proposalUrl
        ? `Include this exact proposal link in the email body: ${proposalUrl}`
        : `Include a proposal link placeholder.`) +
      ` Quote ID: ${quoteRef}.`;

    setEmailLoading(true);
    try {
      const authToken = localStorage.getItem("authToken") || "";
      const resp = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/runs/${backendId}/chat`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body:    JSON.stringify({ message, history: [] }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const reply: string = data.reply || data.email_draft?.body || "";

      if (data.email_draft) {
        // Legacy: structured email_draft field
        const d = data.email_draft;
        setEmailDraft({ toAddr: d.to_addr ?? "", subject: d.subject ?? "", body: d.body ?? "" });
      } else if (data.pending_confirmation?.action_type === "draft_email" && data.pending_confirmation?.params) {
        // Standard path: draft returned via pending_confirmation
        const p = data.pending_confirmation.params;
        setEmailDraft({ toAddr: p.to_addr ?? "", subject: p.subject ?? "", body: p.body ?? "" });
      } else {
        // Fallback: parse Subject: / body from plain-text reply
        const parsed = parseEmailFromReply(reply);
        if (parsed) {
          setEmailDraft({ subject: parsed.subject, body: parsed.body });
        }
      }
    } catch {/* silent */} finally {
      setEmailLoading(false);
    }
  };

  // Called by InlineChatQuotes when a proposal starts generating — shows loading bubble.
  const handleProposalStarted = (q: QuoteResult) => {
    const qk = q.quoteId || q.carrierId || "";
    setProposalGenerating(qk);
  };

  // Called by InlineChatQuotes once a proposal URL is received from the backend.
  // Clears the loading bubble and renders a ProposalDocCard in chat.
  const handleProposalGenerated = (q: QuoteResult, url: string) => {
    const qk = q.quoteId || q.carrierId || "";
    setProposalGenerating(null);
    setProposalUrlsByQuote(prev => ({ ...prev, [qk]: url }));
    setProposalCards(prev => {
      // replace if already exists for this quote, else append
      const exists = prev.findIndex(c => c.quoteKey === qk);
      const card = { carrier: q.carrierName ?? "Carrier", url, quoteKey: qk };
      if (exists >= 0) {
        const next = [...prev];
        next[exists] = card;
        return next;
      }
      return [...prev, card];
    });
  };



  // ── Unified right pane — PLRChatView + structural cards ─────────────────
  const rightPane = (
    <div className="rql-cpane">
      <div className="rql-cpane__thread">

        {/* ── Email draft — opens at the bottom when user triggers it ── */}
        {emailDraft && (
          <EmailDraftCard
            draft={emailDraft}
            backendRunId={_earlyRunIdRef.current ? getBackendId(_earlyRunIdRef.current) : null}
            onClose={() => setEmailDraft(null)}
            onSent={() => setEmailDraft(null)}
          />
        )}

        {/* ── Hard-failed (before submission attempt) ── */}
        {isFailed && !submitted && (() => {
          const failedStep = run.steps.find(s => s.status === "failed");
          const errMsg = failedStep?.error ?? "";
          const isMcpError = /mcp|tool.*not found|not configured|rate mcp|mcp server/i.test(errMsg);
          return (
            <div className="rql-chat__msg rql-chat__msg--ai">
              <span className="rql-chat__avatar"><RiSparklingFill size={11} /></span>
              <div className="rql-cql rql-cql--error">
                <div className="rql-cql__header">
                  <FiAlertTriangle size={12} />
                  <span className="rql-cql__title">{isMcpError ? "MCP Server Error" : "Workflow failed"}</span>
                </div>
                {isMcpError ? (
                  <>
                    <p className="rql-cql__msg">
                      Your Rate MCP server is not configured correctly. Please go to <strong>Settings → MCP Servers</strong> and verify your Rate MCP server URL and API key.
                    </p>
                    {errMsg && (
                      <p style={{ margin: '4px 0 8px', fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {errMsg}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="rql-cql__msg">
                    {errMsg || failedStep?.description || "An error occurred during carrier submission."}
                  </p>
                )}
                <div className="rql-cql__footer" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" className="rql-iwf__cancel" onClick={onBack}>
                    <FiChevronLeft size={11} /> Back to history
                  </button>
                  {isMcpError && (
                    <a
                      href="/settings/mcp"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <FiZap size={11} /> Go to MCP Servers
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* PLRChatView — always render once a valid backend run ID exists */}
        {(() => {
          const bid = _earlyRunIdRef.current ? getBackendId(_earlyRunIdRef.current) : null;
          if (!bid || bid.startsWith('run-')) return null;
          return (
            <PLRChatView
              runId={bid}
              insuredName={run.label?.replace(/^(New|Renewal)\s*[—-]\s*/i, "").trim()}
              runStatus={run.status}
              onSubmitField={(rId, stepId, vals) => submitStepInput(rId, stepId, vals)}
            />
          );
        })()}

        {/* Quote Summary PDF card removed — the agent chat message
             already shows the download button inline via markdown link. */}

        <div ref={chatBottomRef} />
      </div>

    </div>
  );

  if (isTransitioning) {
    return (
      <div className="rql-iwf rql-iwf--skeleton">
        {/* Top bar */}
        <div className="rql-iwf__head">
          <div className="rql-iwf__topbar">
            <div className="rql-skel rql-skel--btn" />
            <div className="rql-skel rql-skel--meta" />
          </div>
        </div>

        <div className="rql-iwf__body">
          {/* ── Left stepper ── */}
          <div className="rql-iwf__stepper">
            {[
              { titleW: 68, subs: [70, 50, 85] },
              { titleW: 80, subs: [60, 75, 55, 90] },
              { titleW: 72, subs: [65, 48] },
            ].map((grp, gi) => (
              <div key={gi} className="rql-skel-group" style={{ animationDelay: `${gi * 0.1}s` }}>
                <div className="rql-skel-step rql-skel-step--head">
                  <div className="rql-skel-step__circle" />
                  <div className="rql-skel-step__title" style={{ width: `${grp.titleW}%` }} />
                  <div className="rql-skel-step__badge" />
                </div>
                {grp.subs.map((w, si) => (
                  <div key={si} className="rql-skel-step rql-skel-step--sub">
                    <div className="rql-skel-step__dot" />
                    <div className="rql-skel-step__bar" style={{ width: `${w}%` }} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* ── Right chat pane ── */}
          <div className="rql-iwf__cpane rql-cpane">
            <div className="rql-skel-chat">
              {/* AI bubble 1 */}
              <div className="rql-skel-chat__msg rql-skel-chat__msg--ai">
                <div className="rql-skel-chat__avatar" />
                <div className="rql-skel-chat__bubble">
                  <div className="rql-skel rql-skel--line" style={{ width: '78%' }} />
                  <div className="rql-skel rql-skel--line" style={{ width: '60%' }} />
                  <div className="rql-skel rql-skel--line" style={{ width: '44%' }} />
                </div>
              </div>
              {/* User bubble */}
              <div className="rql-skel-chat__msg rql-skel-chat__msg--user">
                <div className="rql-skel-chat__bubble rql-skel-chat__bubble--user">
                  <div className="rql-skel rql-skel--line" style={{ width: '55%' }} />
                  <div className="rql-skel rql-skel--line" style={{ width: '38%' }} />
                </div>
              </div>
              {/* AI bubble 2 */}
              <div className="rql-skel-chat__msg rql-skel-chat__msg--ai">
                <div className="rql-skel-chat__avatar" />
                <div className="rql-skel-chat__bubble">
                  <div className="rql-skel rql-skel--line" style={{ width: '82%' }} />
                  <div className="rql-skel rql-skel--line" style={{ width: '65%' }} />
                  <div className="rql-skel rql-skel--line" style={{ width: '50%' }} />
                </div>
              </div>
              {/* Quote card skeleton */}
              <div className="rql-skel-quote-card">
                <div className="rql-skel-quote-card__head">
                  <div className="rql-skel-quote-card__badge" />
                  <div className="rql-skel-quote-card__name" />
                  <div className="rql-skel-quote-card__chip" />
                </div>
                <div className="rql-skel-quote-card__pricing">
                  <div className="rql-skel-quote-card__price" />
                  <div className="rql-skel-quote-card__price rql-skel-quote-card__price--sm" />
                </div>
                <div className="rql-skel-quote-card__actions">
                  <div className="rql-skel rql-skel--chip" />
                  <div className="rql-skel rql-skel--chip" />
                  <div className="rql-skel rql-skel--chip" />
                </div>
              </div>
              {/* Loading spinner */}
              <div className="rql-skel-chat__spinner">
                <FiRefreshCw size={12} className="rql-row-execute__spin" />
                <span>Loading workflow…</span>
              </div>
            </div>
            {/* Footer */}
            <div className="rql-skel-footer">
              <div className="rql-skel rql-skel--chip" />
              <div className="rql-skel rql-skel--chip" />
              <div className="rql-skel rql-skel--chip" />
              <div className="rql-skel rql-skel--inputbar" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rql-iwf">
      <div className="rql-iwf__head">
        <div className="rql-iwf__topbar">
          <div className="rql-iwf__topbar-left">
            <button type="button" className="rql-iwf__back" onClick={onBack}>
              <FiChevronLeft size={14} />
              Back to results
            </button>
            {run && (run.status === 'completed' || run.status === 'failed') && (
              <button type="button" className="rql-iwf__go-to-rate" onClick={async () => {
                try {
                  const bid = getBackendId(run.id);
                  const tok = localStorage.getItem('authToken') || '';
                  const res = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting/runs/${bid}/rate-url`, { headers: { Authorization: `Bearer ${tok}` } });
                  if (res.ok) { const d = await res.json(); if (d.rate_url) window.open(d.rate_url, '_blank'); }
                } catch { /* silently fail */ }
              }}>
                <RateMIcon size={14} /> Go to Rate
              </button>
            )}
          </div>
          <div className="rql-iwf__topbar-meta">
            <span
              className={`rql-iwf__status-dot rql-iwf__status-dot--${run.status}`}
            />
            <span className="rql-iwf__run-label">{run.label}</span>
            <span className="rql-iwf__sep">·</span>
            <FiClock size={10} />
            <span className="rql-iwf__run-time">
              {run.startedAt.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
              {" · "}
              {run.startedAt.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
            <span className="rql-iwf__step-count">
              {completedCt}/{total} steps
            </span>
          </div>
        </div>

        {isPaused && !submitted && !review && (
          <div className="rql-iwf__pause-banner">
            <FiHelpCircle size={14} />
            <div className="rql-iwf__pause-banner-text">
              <strong>Workflow paused — your input is required</strong>
              <span>
                The AI needs information from you before it can continue. Provide your answer on the right.
              </span>
            </div>
          </div>
        )}
        {isPaused && !submitted && review && (
          <div className="rql-iwf__pause-banner">
            <FiHelpCircle size={14} />
            <div className="rql-iwf__pause-banner-text">
              <strong>Workflow paused — review required</strong>
              <span>
                The AI has gathered all the information and is ready to submit
                to carriers. Review the details below and confirm to proceed.
              </span>
            </div>
          </div>
        )}
        {isSubmittingNow && (
          <div className="rql-iwf__pause-banner rql-iwf__pause-banner--info">
            <FiRefreshCw size={14} className="rql-row-execute__spin" />
            <div className="rql-iwf__pause-banner-text">
              <strong>Submitting to carriers</strong>
              <span>
                Quote requests sent — collecting premiums. Results will appear
                here automatically, no refresh needed.
              </span>
            </div>
          </div>
        )}
        {isCompleted && (
          <div className="rql-iwf__pause-banner rql-iwf__pause-banner--success">
            <FiCheck size={14} />
            <div className="rql-iwf__pause-banner-text">
              <strong>Quotes received</strong>
              <span>All carrier responses collected. Review quotes below.</span>
            </div>
          </div>
        )}
      </div>

      <div className="rql-iwf__scroll">
        <div className="rql-iwf__body">
          <div className="rql-iwf__stepper">
            <div className="rql-iwf__stepper-title">Workflow Steps</div>
            {run.steps.map((step, idx) => {
              const isLast = idx === run.steps.length - 1;
              return (
                <div
                  key={step.id}
                  className={`rql-iwf__step rql-iwf__step--${step.status}`}
                >
                  <div className="rql-iwf__step-rail">
                    <span className="rql-iwf__step-dot">
                      <StepIcon status={step.status} index={idx} />
                    </span>
                    {!isLast && (
                      <span
                        className={`rql-iwf__step-line rql-iwf__step-line--${step.status}`}
                      />
                    )}
                  </div>
                  <div className="rql-iwf__step-body">
                    <div className="rql-iwf__step-head">
                      <div className="rql-iwf__step-info">
                        <span className="rql-iwf__step-eyebrow">
                          Step {idx + 1}
                        </span>
                        <span className="rql-iwf__step-label">
                          {step.label}
                        </span>
                      </div>
                      <span
                        className={`rql-iwf__step-pill rql-iwf__step-pill--${step.status}`}
                      >
                        {STEP_PILL_LABEL[step.status]}
                      </span>
                    </div>
                    {step.description && (
                      <div className="rql-iwf__step-desc">
                        {step.description}
                      </div>
                    )}
                    {step.progress && step.progress.length > 0 && (
                      <ul className="rql-iwf__substeps">
                        {(() => {
                          const seen = new Set<string>();
                          return step.progress!.filter(it => {
                            if (seen.has(it.key)) return false;
                            seen.add(it.key);
                            return true;
                          });
                        })().map((it, i) => (
                          <li
                            key={i}
                            className={`rql-iwf__substep rql-iwf__substep--${it.status}`}
                          >
                            <span className="rql-iwf__substep-icon">
                              {it.status === "running" && (
                                <FiRefreshCw
                                  size={9}
                                  className="rql-row-execute__spin"
                                />
                              )}
                              {it.status === "completed" && (
                                <FiCheck size={9} />
                              )}
                              {it.status === "failed" && (
                                <FiAlertTriangle size={9} />
                              )}
                            </span>
                            <span className="rql-iwf__substep-content">
                              <span className="rql-iwf__substep-label">
                                {it.label}
                              </span>
                              {it.detail && (
                                <span className="rql-iwf__substep-detail">
                                  {it.detail}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {rightPane}
        </div>
      </div>
    </div>
  );
};

export default InlineWorkflowPanel;

